/**
 * An unsaved edit of a document, kept in this tab while it is unsaved (stage
 * 3). The app's router cannot hold a navigation back (no data router, so no
 * useBlocker), so an edit left behind by switching project or brand, leaving
 * Workshop or reloading stays here, keyed by the document and the version it
 * started from, and the document offers it back when it opens again. Storage
 * that is off only means there is nothing to offer.
 */
export type StashedDraft = {
  artifactId: string;
  /** The version the edit started from. */
  baseVersion: number;
  title: string;
  content: string;
  note: string;
  savedAt: string;
};

function prefix(viewerId: string | null, artifactId: string): string {
  return `workshop-doc-draft:${viewerId ?? "signed-out"}:${artifactId}:`;
}

function keysFor(viewerId: string | null, artifactId: string): string[] {
  const start = prefix(viewerId, artifactId);
  const keys: string[] = [];
  for (let i = 0; i < sessionStorage.length; i += 1) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(start)) keys.push(key);
  }
  return keys;
}

/** Keep the edit, replacing whatever was kept for this document before. */
export function stashDraft(viewerId: string | null, draft: Omit<StashedDraft, "savedAt">): void {
  try {
    for (const key of keysFor(viewerId, draft.artifactId)) sessionStorage.removeItem(key);
    const value: StashedDraft = { ...draft, savedAt: new Date().toISOString() };
    sessionStorage.setItem(`${prefix(viewerId, draft.artifactId)}v${draft.baseVersion}`, JSON.stringify(value));
  } catch {
    /* storage may be off or full: nothing to offer later */
  }
}

export function forgetDraft(viewerId: string | null, artifactId: string): void {
  try {
    for (const key of keysFor(viewerId, artifactId)) sessionStorage.removeItem(key);
  } catch {
    /* storage may be off */
  }
}

/** The edit kept for this document, if any. */
export function readDraft(viewerId: string | null, artifactId: string): StashedDraft | null {
  try {
    const [key] = keysFor(viewerId, artifactId);
    if (!key) return null;
    const value = JSON.parse(sessionStorage.getItem(key) ?? "null") as Partial<StashedDraft> | null;
    if (
      !value ||
      value.artifactId !== artifactId ||
      typeof value.content !== "string" ||
      typeof value.title !== "string" ||
      typeof value.baseVersion !== "number"
    ) {
      return null;
    }
    return {
      artifactId,
      baseVersion: value.baseVersion,
      title: value.title,
      content: value.content,
      note: typeof value.note === "string" ? value.note : "",
      savedAt: typeof value.savedAt === "string" ? value.savedAt : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}
